/**
 * Abbreviations utility for condensing text in PDFs and other displays
 * 
 * Centralized abbreviation maps that can be used throughout the app
 * for consistent shortening of common terms.
 */

/**
 * Organization name abbreviations
 * Used primarily for PDF generation where space is limited
 */
export const ORGANIZATION_ABBREVIATIONS = {
  'Hospital': 'Hosp',
  'Center': 'Ctr',
  'Medical': 'Med',
  'Healthcare': 'HC',
  'Health Care': 'HC',
  'Associates': 'Assoc',
  'Association': 'Assn',
  'Corporation': 'Corp',
  'Incorporated': 'Inc',
  'Services': 'Svcs',
  'Service': 'Svc',
  'Department': 'Dept',
  'Laboratory': 'Lab',
  'Laboratories': 'Labs',
  'Rehabilitation': 'Rehab',
  'University': 'Univ',
  'Regional': 'Reg',
  'Community': 'Comm',
  'National': 'Natl',
  'International': 'Intl',
  'Foundation': 'Fdn',
  'Institute': 'Inst',
  'Specialists': 'Spec',
  'Specialist': 'Spec',
  'Professional': 'Prof',
  'Physicians': 'Phys',
  'Physician': 'Phys',
  'Practice': 'Prac',
  'Practices': 'Prac',
  'Surgery': 'Surg',
  'Surgical': 'Surg',
  'Ambulatory': 'Amb',
  'Outpatient': 'Outpt',
  'Diagnostic': 'Diag',
  'Diagnostics': 'Diag',
  'Management': 'Mgmt',
  'Company': 'Co',
  'Limited': 'Ltd',
  'Partners': 'Ptnrs',
  'Partnership': 'Ptnrshp',
  'Group': 'Grp',
  'Network': 'Ntwk',
  'System': 'Sys',
  'Systems': 'Sys',
  'Administrative': 'Admin',
  'Administration': 'Admin',
  'Emergency': 'Emerg',
  'Pediatric': 'Peds',
  'Pediatrics': 'Peds',
  'Orthopedic': 'Ortho',
  'Orthopedics': 'Ortho',
  'Cardiology': 'Cardio',
  'Cardiovascular': 'CV',
  'Gastroenterology': 'GI',
  'Dermatology': 'Derm',
  'Oncology': 'Onc',
  'Radiology': 'Rad',
  'Pathology': 'Path',
  'Neurology': 'Neuro',
  'Psychology': 'Psych',
  'Psychiatry': 'Psych',
  'Physical Therapy': 'PT',
  'Occupational Therapy': 'OT',
};

/**
 * Specialty/Taxonomy abbreviations
 * Used for the specialty column in PDFs
 */
export const SPECIALTY_ABBREVIATIONS = {
  // Common terms
  'Internal Medicine': 'Int Med',
  'Family Medicine': 'Fam Med',
  'Emergency Medicine': 'Emerg Med',
  'Physical Medicine': 'Phys Med',
  'Nuclear Medicine': 'Nuc Med',
  'Preventive Medicine': 'Prev Med',
  'Aerospace Medicine': 'Aero Med',
  'Undersea Medicine': 'Undersea Med',
  
  // Specializations
  'Clinical Cardiac Electrophysiology': 'Cardiac EP',
  'Interventional Cardiology': 'Interv Cardio',
  'Cardiovascular Disease': 'CV Disease',
  'Pulmonary Disease': 'Pulm Disease',
  'Infectious Disease': 'Infect Disease',
  'Critical Care Medicine': 'Crit Care',
  'Hospice and Palliative Medicine': 'Hospice/Pall',
  'Sports Medicine': 'Sports Med',
  'Sleep Medicine': 'Sleep Med',
  'Pain Medicine': 'Pain Med',
  'Addiction Medicine': 'Addiction Med',
  'Geriatric Medicine': 'Geriatric Med',
  'Adolescent Medicine': 'Adolesc Med',
  'Neonatal-Perinatal Medicine': 'Neonatal Med',
  
  // Surgery types
  'General Surgery': 'Gen Surg',
  'Thoracic Surgery': 'Thor Surg',
  'Vascular Surgery': 'Vasc Surg',
  'Plastic Surgery': 'Plast Surg',
  'Colon and Rectal Surgery': 'Colorectal Surg',
  'Neurological Surgery': 'Neuro Surg',
  'Orthopaedic Surgery': 'Ortho Surg',
  'Orthopedic Surgery': 'Ortho Surg',
  
  // Other specialties
  'Diagnostic Radiology': 'Diag Rad',
  'Interventional Radiology': 'Interv Rad',
  'Radiation Oncology': 'Rad Onc',
  'Medical Oncology': 'Med Onc',
  'Gynecologic Oncology': 'Gyn Onc',
  'Hematology & Oncology': 'Hem/Onc',
  'Hematology/Oncology': 'Hem/Onc',
  'Obstetrics & Gynecology': 'OB/GYN',
  'Obstetrics and Gynecology': 'OB/GYN',
  'Reproductive Endocrinology': 'Repro Endo',
  'Pediatric Cardiology': 'Peds Cardio',
  'Pediatric Pulmonology': 'Peds Pulm',
  'Pediatric Gastroenterology': 'Peds GI',
  'Pediatric Hematology-Oncology': 'Peds Hem/Onc',
  'Pediatric Nephrology': 'Peds Nephro',
  'Pediatric Endocrinology': 'Peds Endo',
  'Child & Adolescent Psychiatry': 'Child Psych',
  'Allergy and Immunology': 'Allergy/Immun',
  'Allergy & Immunology': 'Allergy/Immun',
  'Rheumatology': 'Rheum',
  'Endocrinology': 'Endo',
  'Nephrology': 'Nephro',
  'Gastroenterology': 'GI',
  'Pulmonology': 'Pulm',
  'Anesthesiology': 'Anes',
  'Otolaryngology': 'ENT',
  'Ophthalmology': 'Ophthal',
  'Dermatology': 'Derm',
  'Urology': 'Urol',
  'Neurology': 'Neuro',
  'Psychiatry': 'Psych',
  'Pathology': 'Path',
  'Radiology': 'Rad',
  
  // Generic terms
  'Specialist': 'Spec',
  'Specialists': 'Spec',
  'Rehabilitation': 'Rehab',
  'Therapy': 'Ther',
  'Therapist': 'Ther',
  'Physical Therapy': 'PT',
  'Occupational Therapy': 'OT',
  'Speech Therapy': 'ST',
  'Respiratory Therapy': 'RT',
};

/**
 * Abbreviate an organization name using the standard abbreviation map
 * 
 * @param {string} name - The organization name to abbreviate
 * @param {Object} options - Options for abbreviation
 * @param {boolean} options.preserveCase - If true, tries to preserve original case (default: false)
 * @returns {string} - The abbreviated name
 */
export const abbreviateOrgName = (name, options = {}) => {
  if (!name) return name;
  
  let result = name;
  
  // Sort by length (longest first) to handle multi-word phrases before single words
  const sortedAbbreviations = Object.entries(ORGANIZATION_ABBREVIATIONS)
    .sort((a, b) => b[0].length - a[0].length);
  
  sortedAbbreviations.forEach(([full, abbrev]) => {
    // Use word boundary regex to avoid partial word replacements
    // Case insensitive matching
    const regex = new RegExp(`\\b${full}\\b`, 'gi');
    result = result.replace(regex, abbrev);
  });
  
  return result;
};

/**
 * Check if abbreviation would significantly shorten the name
 * Useful for deciding whether to abbreviate or not
 * 
 * @param {string} name - The organization name
 * @param {number} threshold - Minimum characters to save (default: 5)
 * @returns {boolean} - True if abbreviation saves at least threshold characters
 */
export const shouldAbbreviate = (name, threshold = 5) => {
  if (!name) return false;
  const abbreviated = abbreviateOrgName(name);
  return (name.length - abbreviated.length) >= threshold;
};

/**
 * Abbreviate a specialty/taxonomy name using the specialty abbreviation map
 * 
 * @param {string} name - The specialty name to abbreviate
 * @returns {string} - The abbreviated specialty name
 */
export const abbreviateSpecialty = (name) => {
  if (!name) return name;
  
  let result = name;
  
  // Sort by length (longest first) to handle multi-word phrases before single words
  const sortedAbbreviations = Object.entries(SPECIALTY_ABBREVIATIONS)
    .sort((a, b) => b[0].length - a[0].length);
  
  sortedAbbreviations.forEach(([full, abbrev]) => {
    // Use word boundary regex to avoid partial word replacements
    // Case insensitive matching
    const regex = new RegExp(`\\b${full}\\b`, 'gi');
    result = result.replace(regex, abbrev);
  });
  
  return result;
};

export default {
  ORGANIZATION_ABBREVIATIONS,
  SPECIALTY_ABBREVIATIONS,
  abbreviateOrgName,
  abbreviateSpecialty,
  shouldAbbreviate,
};